/**
 * Classifies player error codes as recoverable (worth retrying playback)
 * or non-recoverable (content-level restriction, format mismatch, DRM),
 * and further splits recoverable errors into auto-retryable (transient,
 * likely to resolve without user action) vs. manual-only.
 *
 * JW Player errors are proxied through the SVP player's `error` event
 * (see player/src/js/player/playback/provider.ts → onError), so a single
 * `usePlayerEvent('error', ...)` listener receives both SVP-originated
 * error codes (1xxxx) and JW error codes (2xxxxx).
 *
 * SVP error codes
 * ---------------
 * 12000 – Stream active (transient)        → recoverable, manual only
 * 12001 – Preview mode                     → non-recoverable (access restriction)
 * 14001 – Stream not active                → non-recoverable (content state)
 * 14002 – Geoblock                         → non-recoverable (access restriction)
 * 14003 – Stream not found                 → non-recoverable (content missing)
 * 14004 – Stream unpublished               → non-recoverable (content missing)
 * 14005 – Incorrect token                  → recoverable, AUTO-RETRY
 * 15000 – General error                    → recoverable, manual only
 * 15101 – Network error                    → recoverable, AUTO-RETRY
 * 15102 – Token fetch error                → non-recoverable (paywall takes over)
 * 15103 – Not authenticated                → non-recoverable (paywall takes over)
 *
 * JW Player error codes (proxied through SVP)
 * --------------------------------------------
 * 220001 – Live down or has ended            → recoverable, AUTO-RETRY
 * 221000 – Unknown network error (HTML5)     → recoverable, AUTO-RETRY
 * 224000–224002 – Unknown/aborted/decode     → recoverable, manual only
 * 224003 – Source not supported by browser   → non-recoverable (capability)
 * 225xxx, 226xxx – Fairplay DRM errors       → non-recoverable (DRM/protected)
 * 230001, 220001 – Live stream stalled       → recoverable, manual only
 * 230002 – HLS offline stall                 → recoverable, AUTO-RETRY
 * 232002 – Manifest fetch offline            → recoverable, AUTO-RETRY
 * 232011, 232012 – Crossdomain/HTTPS         → non-recoverable (config)
 * 232632, 242632 – Unsupported codecs        → non-recoverable (capability)
 * 233000 – Unknown media loading error       → recoverable, AUTO-RETRY
 * 233001 – Media request timeout             → recoverable, AUTO-RETRY
 * 233011, 233012 – Crossdomain/HTTPS         → non-recoverable (config)
 * 234001 – HLS buffer stall                  → recoverable, AUTO-RETRY
 * 234002 – Buffer append error               → recoverable, manual only
 * 244001 – 244003 – Video element DASH error → recoverable, manual only
 * 246014 – DRM license has expired           → recoverable, AUTO-RETRY
 * 246xxx (other) – Shaka DRM errors          → non-recoverable (DRM)
 */

const NON_RECOVERABLE_SVP_CODES = new Set([
  12001, // Preview mode — access restriction
  14001, // Stream not active
  14002, // Geoblock
  14003, // Stream not found
  14004, // Stream unpublished
  15102, // Token fetch error
  15103, // Not authenticated
]);

/**
 * SVP / JW error codes that should trigger an automatic silent retry.
 * All are transient failures likely to resolve without user action.
 * Membership here also implies recoverable — see `isRecoverableError`,
 * which treats this set as an override even when a code otherwise falls
 * inside a non-recoverable set/range (e.g. 246014 within the Shaka DRM
 * range).
 */
const AUTO_RETRY_CODES = new Set([
  // SVP
  14005, // Incorrect/expired CDN token
  15101, // Network error
  // JW (proxied through SVP)
  220001, // Live down or has ended (happens on Safari inactivity)
  221000, // Unknown network error (HTML5 provider)
  230002, // HLS playback stalled after going offline
  232002, // Manifest request failed due to being offline
  233000, // Unknown media loading error
  233001, // Media request timeout
  234001, // HLS playback stalled — not enough buffered media
  246014, // DRM license has expired — token refresh on recovery fixes it
  241001, // A DASH-related network request timed out.
]);

const NON_RECOVERABLE_JW_CODES = new Set([
  224003, // Media source not supported by this browser
  232011, // Crossdomain access denied (manifest)
  232012, // HTTP resource over HTTPS (manifest)
  232632, // Incompatible codec in HLS renditions
  233011, // Crossdomain access denied (media)
  233012, // HTTP resource over HTTPS (media)
  242632, // Incompatible codec in DASH manifest
]);

/**
 * JW error code ranges that are non-recoverable.
 * Each entry is an inclusive [min, max] range.
 */
const NON_RECOVERABLE_JW_RANGES: [number, number][] = [
  [225000, 225999], // Fairplay server certificate errors
  [226000, 226999], // Fairplay license errors
  [246000, 246999], // Shaka DRM errors
];

const isInNonRecoverableRange = (code: number): boolean =>
  NON_RECOVERABLE_JW_RANGES.some(([min, max]) => code >= min && code <= max);

/**
 * Returns true if the player should automatically attempt recovery for this
 * error code without requiring user interaction.
 */
export const isAutoRetryableError = (code: number | undefined): boolean => {
  if (code === undefined) {
    return false;
  }
  return AUTO_RETRY_CODES.has(code);
};

/**
 * Returns true if a "Try again" / restart-playback button should be shown
 * for the given error code. Returns true for unknown codes (undefined) since
 * offering a retry is safe in that case.
 */
export const isRecoverableError = (code: number | undefined): boolean => {
  if (code === undefined) {
    return true;
  }
  if (AUTO_RETRY_CODES.has(code)) {
    return true;
  }
  if (NON_RECOVERABLE_SVP_CODES.has(code)) {
    return false;
  }
  if (NON_RECOVERABLE_JW_CODES.has(code)) {
    return false;
  }
  if (isInNonRecoverableRange(code)) {
    return false;
  }
  return true;
};

/**
 * SVP error codes indicating an entitlement failure (the viewer is either
 * anonymous or lacks the required subscription), which should route to the
 * paywall UI rather than a generic error state.
 */
export const ENTITLEMENT_ERROR_CODES = new Set([15102, 15103]);

export const isEntitlementError = (code: number | undefined): boolean =>
  code !== undefined && ENTITLEMENT_ERROR_CODES.has(code);
